import { css } from '@emotion/core';
import { useEffect, useMemo, useState } from 'react';

const addFontFace = (fontFace: FontFace) => `
    @font-face {
      font-family: ${fontFace.fontFamily};
      src: ${fontFace.fontUrl};
      ${fontFace.fontWeight ? `font-weight: ${fontFace.fontWeight};` : ''}
      font-display: swap;
    }
`;

type FontFormat = 'truetype' | 'opentype' | 'woff' | 'woff2';

const extToFormatMap = {
  woff2: 'woff2',
  woff: 'woff',
  otf: 'opentype',
  ttf: 'truetype',
} as const;

type FontExtension = keyof typeof extToFormatMap;

const getFontFormat = (fileUrl: string): FontFormat => {
  const parts = fileUrl.split('.');
  const extension = parts[parts.length - 1] as FontExtension;
  return extToFormatMap[extension];
};

const getCDNPath = (FONTS_CDN: string, fileUrl: string) => `${FONTS_CDN}/${fileUrl}`;

const getFontFaceUrlFallback = (FONTS_CDN: string, fileUrls: string[]) =>
  // eslint-disable-next-line implicit-arrow-linebreak
  fileUrls.map((fileUrl) => `url('${getCDNPath(FONTS_CDN, fileUrl)}') format('${getFontFormat(fileUrl)}')`).join(', ');

const importFont = async (FONTS_CDN: string, {
  fontFamily,
  fontWeight,
  assetUrls,
}: FontFaceRequest): Promise<FontFace> => {
  const fontUrl = getFontFaceUrlFallback(FONTS_CDN, assetUrls);
  const font = new FontFace(fontFamily, fontUrl, {
    weight: fontWeight,
  });

  await font.load();
  document.fonts.add(font);

  return {
    fontFamily,
    fontWeight,
    fontUrl,
  };
};

export type FontFaceRequest = {
  fontFamily: string;
  assetUrls: string[];
  fontWeight?: string;
}

export type FontFace = {
  fontFamily: string;
  fontUrl: string;
  fontWeight?: string;
}

export const useFontFaces = (FONTS_CDN: string, fonts: FontFaceRequest[]) => {
  const [ loading, setLoading ] = useState(true);
  const [ fontsLoaded, setFontsLoaded ] = useState<FontFace[]>([]);

  const loadFonts = async () => {
    setLoading(true);
    const results = await Promise.allSettled(fonts.map((font) => importFont(FONTS_CDN, font)));
    const loadedFonts = results
      .filter((x): x is PromiseFulfilledResult<FontFace> => x.status === 'fulfilled')
      .map((x) => x.value);

    if (import.meta.env.MODE !== 'production') {
      // eslint-disable-next-line no-console
      console.log('font faces loaded', loadedFonts);
    }

    setFontsLoaded(loadedFonts);
    setLoading(false);
  };

  useEffect(() => {
    loadFonts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fontsStyle = useMemo(
    () => css`${fontsLoaded.map((font) => addFontFace(font)).join('\n')}`,
    [ fontsLoaded ]
  );

  return {
    loading,
    fontsStyle,
  };
};
